import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from 'react-router';
import { setUnauthorizedHandler } from '@/api/client';
import { CookieNotice } from '@/components/ui/CookieNotice';
import { ConfirmHost, Toaster } from '@/components/ui/Feedback';
import { FullPageSpinner } from '@/components/ui/Spinner';
import { endSessionLocally } from '@/lib/session';
import { AppLayout } from '@/pages/AppLayout';
import { ForgotPasswordPage, LoginPage, RegisterPage, ResetPasswordPage, VerifyEmailGate, VerifyEmailPage } from '@/pages/AuthPages';
import { ConversationPage } from '@/pages/ConversationPage';
import { HomePage } from '@/pages/HomePage';
import { InvitePage } from '@/pages/InvitePage';
import { GiftPage } from '@/pages/GiftPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ServerPage } from '@/pages/ServerPage';
import { useAuth } from '@/stores/auth';
import { toast } from '@/stores/ui';

// Policies and licences are rarely opened, so they load on demand.
const legal = () => import('@/pages/LegalPages');
const PrivacyPage = lazy(() => legal().then((m) => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => legal().then((m) => ({ default: m.TermsPage })));
const CookiesPage = lazy(() => legal().then((m) => ({ default: m.CookiesPage })));
const LicensesPage = lazy(() => legal().then((m) => ({ default: m.LicensesPage })));

function Root() {
  return (
    <>
      <Outlet />
      <CookieNotice />
    </>
  );
}

function LegalRoute() {
  return (
    <Suspense fallback={<FullPageSpinner />}>
      <Outlet />
    </Suspense>
  );
}

function RequireAuth() {
  const status = useAuth((s) => s.status);
  const mustConfirmEmail = useAuth((s) => s.config.emailVerification === 'required' && s.user?.emailVerified === false);
  const location = useLocation();
  if (status === 'idle' || status === 'loading') return <FullPageSpinner />;
  if (status === 'unauthenticated') {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (mustConfirmEmail) return <VerifyEmailGate />;
  return <Outlet />;
}

function PublicOnly() {
  const status = useAuth((s) => s.status);
  const location = useLocation();
  if (status === 'idle' || status === 'loading') return <FullPageSpinner />;
  if (status === 'authenticated') {
    const next = new URLSearchParams(location.search).get('next');
    // Only follow same-app paths: "//site" and "/\site" would point elsewhere (prevents open redirects).
    const safe = next && /^\/(?![/\\])/.test(next) && !/[\u0000-\u001f\\]/.test(next);
    return <Navigate to={safe ? next : '/app/@me'} replace />;
  }
  return <Outlet />;
}

const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      { path: '/', element: <Navigate to="/app/@me" replace /> },
      {
        // Readable whether or not you're signed in.
        element: <LegalRoute />,
        children: [
          { path: '/privacy', element: <PrivacyPage /> },
          { path: '/terms', element: <TermsPage /> },
          { path: '/cookies', element: <CookiesPage /> },
          { path: '/licenses', element: <LicensesPage /> },
        ],
      },
      {
        element: <PublicOnly />,
        children: [
          { path: '/login', element: <LoginPage /> },
          { path: '/register', element: <RegisterPage /> },
          { path: '/forgot-password', element: <ForgotPasswordPage /> },
        ],
      },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/verify-email', element: <VerifyEmailPage /> },
      {
        element: <RequireAuth />,
        children: [
          { path: '/invite/:code', element: <InvitePage /> },
          { path: '/gift/:code', element: <GiftPage /> },
          {
            path: '/app',
            element: <AppLayout />,
            children: [
              { index: true, element: <Navigate to="/app/@me" replace /> },
              { path: '@me', element: <HomePage /> },
              { path: '@me/:conversationId', element: <ConversationPage /> },
              { path: ':serverId', element: <ServerPage /> },
              { path: ':serverId/:channelId', element: <ServerPage /> },
              { path: ':serverId/:channelId/threads/:threadId', element: <ServerPage /> },
            ],
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  const init = useAuth((s) => s.init);

  useEffect(() => {
    void init();
    setUnauthorizedHandler(() => {
      if (useAuth.getState().status !== 'authenticated') return;
      endSessionLocally();
      toast.info('Your session has expired. Please log in again.');
    });
    return () => setUnauthorizedHandler(null);
  }, [init]);

  return (
    <>
      <RouterProvider router={router} />
      <Toaster />
      <ConfirmHost />
    </>
  );
}
